
// packages/sdk/src/core/connectionPool.ts

import { EventEmitter } from "events";
import Long from "long";
import { request, response } from "../proto/bundle"
import { Request, Response } from "../../src/proto/generated";
import { createTokenSignInRequest, createSignInRequestForExternalUser } from "../ops/userOps/auth";
import { createHash } from "crypto";
import { Task } from "../ops/dataOps/task";
import { Mutex } from "async-mutex";
import ws from "isomorphic-ws";
import { initInProgress } from "../init/sdkInit";
import { L } from "vitest/dist/chunks/environment.d.cL3nLXbE";
import { get } from "http";
import { hashString } from "../utils/hash";

const WebSocketImpl = typeof WebSocket !== "undefined" ? WebSocket : ws;

function uint8ToHex(bytes: Uint8Array): string {
    return Array.from(bytes).map(b => b.toString(16).padStart(2, "0")).join("");
}

// let WebSocketImpl: typeof WebSocket;

// if (typeof WebSocket !== "undefined") {
//     WebSocketImpl = WebSocket;
// } else {
//     WebSocketImpl = require("isomorphic-ws");
// }

interface PooledConnection {
    id: number;
    socket: WebSocket;
    isAlive: boolean;
    pendingTasks: number;
    exclusive: boolean;
    activeFileStreams: Set<string>; // versionUuid hex keys currently uploading chunks
    send: (data: Uint8Array) => void;
    authReject?: (err: Error) => void;
}

const AUTH_TIMEOUT_MS = 30000;
const ACQUIRE_TIMEOUT_MS = 30000;
const REQUEST_TIMEOUT_MS = 60000;

interface ConnectionPoolOptions {
    url: string;
    poolSize: number;
    reconnectDelayMs?: number;
}

export const BLOCK_SIZE = 32 * 1024;
export const CHUNK_SIZE = 4 * 1024 * 1024;
export const MAX_STREAMS_PER_CONNECTION = 4;

export let lastAuthenticatedEmail: string | undefined = undefined;
export function setAuthenticatedEmail(email: string) {
    lastAuthenticatedEmail = email;
}
export function getAuthenticatedEmail(): string {
    if (!lastAuthenticatedEmail) throw new Error("No authenticated email");
    return lastAuthenticatedEmail;
}
export function checkAndThrow(res: response.Response) {
    const status = res?.signIn?.status ?? 1;
    if (status !== 0) {
        throw new Error(res?.signIn?.message || "Unknown error");
    }
}
export function checkAndThrowForSignInSpace(res: response.Response) {
    const status = res?.signInToSpace?.status ?? 1;
    if (status !== 0) {
        throw new Error("Unknown error");
    }
}


// Fallback counter (browser or no Atomics)
let fallbackCounter = 0;

// Node/Electron: shared counter with Atomics
let sharedBuffer: SharedArrayBuffer | null = null;
let counterView: Int32Array | null = null;

if (typeof SharedArrayBuffer !== "undefined" && typeof Atomics !== "undefined") {
    sharedBuffer = new SharedArrayBuffer(4);
    counterView = new Int32Array(sharedBuffer);
}

export function generateRequestId(): Long {
    if (counterView) {
        const newVal = Atomics.add(counterView, 0, 1) + 1;
        return Long.fromNumber(newVal);
    } else {
        fallbackCounter++;
        return Long.fromNumber(fallbackCounter);
    }
}
export function getSignInRequestForExternalUser(): request.SignInToSpace | undefined {
    if (!globalPool) {
        // throw new Error("SDK not initialized. Call sdkInit() first.");
        return undefined;
    }
    return globalPool.getSignInRequestForExternalUser()
}
export class ConnectionPool extends EventEmitter {
    private email!: string;
    private connections: Map<number, PooledConnection> = new Map();
    private url: string;
    private poolSize: number;
    private reconnectDelayMs: number;
    private connectionIdCounter: number = 0;
    private activeConnections: number = 0;
    private pin: string = "";
    private token: Uint8Array = new Uint8Array();
    private connection_mutex = new Mutex();
    private cancelled_requests: Set<Long> = new Set();
    private signInRequestForExternalUser?: request.SignInToSpace; //this field is optional


    private pendingRequests: Map<string, (data: Uint8Array) => void> = new Map();
    private pendingRejecters: Map<string, (err: Error) => void> = new Map();
    private pendingConnIds: Map<string, number> = new Map();

    constructor(opts: ConnectionPoolOptions) {
        super();
        this.url = opts.url;
        this.poolSize = opts.poolSize;
        this.reconnectDelayMs = opts.reconnectDelayMs ?? 3000;

        if (this.listenerCount("error") === 0) {
            this.on("error", () => { /* default handler */ });
        }
    }
    getPin(): string {
        return this.pin;
    }
    getToken(): Uint8Array {
        return this.token;
    }
    getActiveConnections() {
        return this.activeConnections;
    }
    setEmail(email: string) {
        this.email = email;
    }
    getEmail(): string {
        return this.email;
    }
    getPoolSize(): number {
        return this.connections.size;
    }
    getAllConnections(): [number, PooledConnection][] {
        return Array.from(this.connections.entries());
    }
    addCancellledRequest(id: Long) {
        this.cancelled_requests.add(id);
    }
    isCancelled(id: Long): boolean {
        return this.cancelled_requests.has(id);
    }

    async setPin(pin: string, token: Uint8Array) {
        this.token = token;
        this.pin = pin;
        await this.getAvailableConnection();
        // Pre-warm remaining connections in the background so they're ready for uploads
        this.warmUpConnections();
    }

    private warmUpConnections() {
        const toCreate = this.poolSize - this.activeConnections;
        if (toCreate <= 0) return;
        for (let i = 0; i < toCreate; i++) {
            this.createConnection();
        }
    }
    setSignInRequestForExternalUser(signInRequest: request.SignInToSpace) {
        this.signInRequestForExternalUser = signInRequest;
    }
    getSignInRequestForExternalUser(): request.SignInToSpace | undefined {
        return this.signInRequestForExternalUser;
    }



    private createConnection() {
        const id = this.connectionIdCounter++;
        const ws = new WebSocketImpl(this.url) as WebSocket;
        ws.binaryType = "arraybuffer";

        const conn: PooledConnection = {
            id,
            socket: ws,
            isAlive: false,
            pendingTasks: 0,
            exclusive: false,
            activeFileStreams: new Set(),
            send: (data: Uint8Array) => {
                if (ws.readyState === WebSocketImpl.OPEN) {
                    try {
                        ws.send(data);
                    } catch (err) {
                        this.failConnectionPending(id, err as Error);
                    }
                } else {
                    this.failConnectionPending(
                        id,
                        new Error(`Connection ${id} send on non-OPEN socket (state ${ws.readyState})`),
                    );
                }
            }
        };

        ws.onopen = async () => {
            try {
                if (this.signInRequestForExternalUser) {
                    const req = createSignInRequestForExternalUser(this.signInRequestForExternalUser);
                    await this.runAuthHandshake(conn, req, checkAndThrowForSignInSpace);
                }
                else if (this.pin && this.token) {
                    const req = await createTokenSignInRequest(this.pin, this.token);
                    const resp = await this.runAuthHandshake(conn, req, checkAndThrow);
                    this.setEmail(resp.signIn?.email!);
                }
            } catch (error) {
                conn.authReject = undefined;
                this.emit("createFailed", id, error);
                try { ws.close(); } catch { /* noop */ }
                return;
            }
            conn.authReject = undefined;
            this.activeConnections++;
            conn.isAlive = true;
            this.connections.set(id, conn);
            this.emit("open", id);
        };

        ws.onmessage = (event: MessageEvent | { data: ArrayBuffer }) => {
            try {
                const raw = new Uint8Array(event.data);
                const decoded = Response.decode(raw);
                const id = Long.fromValue(decoded.id ?? 0).toString();

                const resolver = this.pendingRequests.get(id);
                if (resolver) {
                    this.unregisterPending(id);
                    resolver(raw);
                } else {
                    this.emit("message", id, raw);
                }
            } catch (e) {
                this.emit("error", -1, e);
            }
        };

        ws.onerror = (e) => {
            if (this.listenerCount("error") > 0) {
                this.emit("error", id, e);
            } else {
                // unhandled WebSocket error
            }
        };

        ws.onclose = () => {
            // Reject any in-flight requests on this connection so callers don't
            // hang forever waiting for a response that will never arrive.
            this.failConnectionPending(id, new Error(`Connection ${id} closed`));

            if (conn.authReject) {
                const reject = conn.authReject;
                conn.authReject = undefined;
                reject(new Error(`Connection ${id} closed during auth`));
                return; // onopen catch will emit createFailed and stop the reconnect storm
            }
            if (!conn.isAlive) {
                // Closed before ever becoming alive (e.g. server rejected the upgrade).
                this.emit("createFailed", id, new Error(`Connection ${id} closed before alive`));
                return;
            }
            this.activeConnections--;
            conn.isAlive = false;
            this.connections.delete(id);
            this.emit("close", id);
            if (this.activeConnections < this.poolSize) {
                setTimeout(() => this.createConnection(), this.reconnectDelayMs);
            }
        };
    }

    private registerPending(
        key: string,
        connId: number,
        resolve: (raw: Uint8Array) => void,
        reject: (err: Error) => void,
    ): void {
        this.pendingRequests.set(key, resolve);
        this.pendingRejecters.set(key, reject);
        this.pendingConnIds.set(key, connId);
    }

    private unregisterPending(key: string): void {
        this.pendingRequests.delete(key);
        this.pendingRejecters.delete(key);
        this.pendingConnIds.delete(key);
    }

    private failConnectionPending(connId: number, err: Error): void {
        const keys: string[] = [];
        for (const [k, c] of this.pendingConnIds) {
            if (c === connId) keys.push(k);
        }
        for (const k of keys) {
            const rej = this.pendingRejecters.get(k);
            this.unregisterPending(k);
            try { rej?.(err); } catch { /* noop */ }
        }
    }

    private async runAuthHandshake(
        conn: PooledConnection,
        req: request.Request,
        validate: (res: response.Response) => void,
    ): Promise<response.Response> {
        const key = Long.fromValue(req.id!).toString();
        const data = Request.encode(req).finish();
        conn.pendingTasks += 1;
        let settled = false;
        const finalize = () => {
            if (settled) return;
            settled = true;
            this.unregisterPending(key);
            conn.pendingTasks -= 1;
            conn.authReject = undefined;
        };

        try {
            const resp = await new Promise<response.Response>((resolve, reject) => {
                const timeout = setTimeout(() => {
                    finalize();
                    reject(new Error(`Auth timeout for connection ${conn.id}`));
                }, AUTH_TIMEOUT_MS);

                conn.authReject = (err: Error) => {
                    clearTimeout(timeout);
                    finalize();
                    reject(err);
                };

                this.registerPending(
                    key,
                    conn.id,
                    (raw) => {
                        clearTimeout(timeout);
                        finalize();
                        try {
                            resolve(Response.decode(raw));
                        } catch (err) {
                            reject(err as Error);
                        }
                    },
                    (err) => {
                        clearTimeout(timeout);
                        finalize();
                        reject(err);
                    },
                );
                conn.send(data);
            });
            validate(resp);
            return resp;
        } finally {
            finalize();
        }
    }

    private waitForConnectionSlot(): Promise<void> {
        return new Promise<void>((resolve) => {
            let settled = false;
            const settle = () => {
                if (settled) return;
                settled = true;
                this.off("open", onOpen);
                this.off("createFailed", onFailed);
                clearTimeout(timeout);
                resolve();
            };
            const onOpen = () => settle();
            const onFailed = () => settle();
            const timeout = setTimeout(settle, ACQUIRE_TIMEOUT_MS);
            this.on("open", onOpen);
            this.on("createFailed", onFailed);
            try {
                this.createConnection();
            } catch {
                settle();
            }
        });
    }

    async getAvailableConnection(exclusive: boolean = false): Promise<PooledConnection | undefined> {
        const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

        while (true) {
            const release = await this.connection_mutex.acquire();
            let needSlot = false;
            try {
                const usableConnections = [...this.connections.values()]
                    .filter(c => c.isAlive && !c.exclusive)
                    .sort((a, b) => a.pendingTasks - b.pendingTasks);
                if (usableConnections.length > 1) {
                    const selected = usableConnections[0];
                    selected.exclusive = exclusive;
                    release();
                    return selected;
                }
                if (usableConnections.length === 1) {
                    // A single usable connection: hand it out for a shared request,
                    // or for an exclusive one when the pool can't grow any further.
                    // Otherwise an exclusive caller (e.g. getChunk) would wait forever
                    // for a second connection that will never open — a small pool, or
                    // Safari stalling concurrent WebSocket opens to the same host.
                    const canGrow = this.activeConnections < this.poolSize;
                    if (!exclusive || !canGrow) {
                        const selected = usableConnections[0];
                        selected.exclusive = exclusive;
                        release();
                        return selected;
                    }
                }

                needSlot = this.activeConnections < this.poolSize;
            } catch (err) {
                release();
                console.error("getAvailableConnection failed:", err);
                return undefined;
            }
            release();

            if (needSlot) {
                await this.waitForConnectionSlot();
            } else {
                await wait(100);
            }
        }
    }

    releaseConnection(conn: PooledConnection) {
        conn.exclusive = false;
    }

    /**
     * Get a connection for putChunk that supports multiplexing:
     * - Up to MAX_STREAMS_PER_CONNECTION concurrent file streams per connection
     * - Never two chunks from the same file on the same connection
     */
    async getConnectionForPutChunk(versionUuidHex: string): Promise<PooledConnection> {
        const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

        while (true) {
            const release = await this.connection_mutex.acquire();
            let needSlot = false;
            try {
                const candidates = [...this.connections.values()]
                    .filter(c => c.isAlive && !c.exclusive
                        && c.activeFileStreams.size < MAX_STREAMS_PER_CONNECTION
                        && !c.activeFileStreams.has(versionUuidHex))
                    .sort((a, b) => a.activeFileStreams.size - b.activeFileStreams.size);

                if (candidates.length > 0) {
                    const selected = candidates[0];
                    selected.activeFileStreams.add(versionUuidHex);
                    release();
                    return selected;
                }

                needSlot = this.activeConnections < this.poolSize;
            } catch (err) {
                release();
                throw err;
            }
            release();

            if (needSlot) {
                await this.waitForConnectionSlot();
            } else {
                await wait(50);
            }
        }
    }

    /**
     * Release a putChunk stream slot on a connection.
     */
    releasePutChunkStream(conn: PooledConnection, versionUuidHex: string) {
        conn.activeFileStreams.delete(versionUuidHex);
    }

    async getChunk(chunkId: Uint8Array, versionUuid: Uint8Array, fileFullPath: string, task: Task, onProgress: any, remaining_chunk_size: number, fileUuId: Uint8Array): Promise<Uint8Array> {
        // console.log("remaining_chunk_size", remaining_chunk_size);
        if (this.isCancelled(task.getId())) {
            throw new Error("Task cancelled");
        }
        const conn = await this.getAvailableConnection(true);
        if (!conn) throw new Error("No available connection");
        let data = [];
        let flag = request.ChunkFlags.START;
        let block_size_for_download = 32 * 1024;
        let remaining = remaining_chunk_size;
        const expectedIds: string[] = [];
        const chunkPromises: Promise<response.Response>[] = [];
        let numOfIterations = 0;

        while (remaining > 0) {
            const current_block_size = Math.min(block_size_for_download, remaining);
            if (current_block_size == 0) {
                break;
            }
            numOfIterations++;
            flag = remaining === remaining_chunk_size ? request.ChunkFlags.START : request.ChunkFlags.NONE;
            remaining -= current_block_size;
            const key = generateRequestId().toString();
            const req_generated_id = Long.fromString(key);
            const getchunk_req = request.Request.create({
                id: req_generated_id,
                getChunk: request.GetChunk.create({
                    uuid: chunkId,
                    versionUuid: versionUuid,
                    flag,
                    fileFullPath,
                    typeOfPath: 0,
                    fileUuid: fileUuId, // as it is not used in the current implementation.
                })
            });

            // Set up the promise and handler immediately after sending
            const promise = new Promise<response.Response>((resolve, reject) => {
                const handler = (raw: Uint8Array) => {
                    try {
                        // console.log("got chunk response for req id ", req_generated_id.toString());
                        const decoded = response.Response.decode(raw);
                        resolve(decoded);
                    } catch (err) {
                        reject(err);
                    }
                };
                this.pendingRequests.set(key, handler);

                setTimeout(() => {
                    if (this.pendingRequests.has(req_id)) {
                        this.pendingRequests.delete(req_id);
                        reject(new Error("Response timeout for id: " + req_id));
                    }
                }, 500000);
            });
            const req_id = this.sendRawRequestWithoutIdInsertion(conn, getchunk_req);


            chunkPromises.push(promise);
            expectedIds.push(req_id);
        }
        // console.log("numOfIterations", numOfIterations);

        // Send dummy getchunk request
        // const dummy_getchunk_req = request.Request.create({
        //     id: null,
        //     getChunk: request.GetChunk.create({
        //         uuid: chunkId,
        //         versionUuid: versionUuid,
        //         flag: request.ChunkFlags.NONE,
        //         fileFullPath,
        //         typeOfPath: 0 // as it is not used in the current implementation.
        //     })
        // });
        // console.log("sent all reqs and expected ids ", expectedIds);

        // let dummy_req_id = this.sendRawRequestWithoutCallBackInsertion(conn, dummy_getchunk_req);

        // Await all chunk promises in order
        for (let i = 0; i < chunkPromises.length; i++) {
            // console.log("awaiting chunk ", i + 1, " of ", chunkPromises.length);
            const resp = await chunkPromises[i];
            // console.log("got chunk ", i + 1, " of ", chunkPromises.length);

            if (resp.getChunk?.status !== 0) {
                this.releaseConnection(conn);
                throw new Error(`Chunk failed with status ${resp.getChunk?.status}`);
            }
            if (resp.getChunk?.data) {
                // console.log("got chunk data of size ", resp.getChunk.data.length);
                data.push(...resp.getChunk.data);
                if (i === 0) {
                    // console.log("first chunk size", resp.getChunk.data.length);
                }
                task.current_size += resp.getChunk.data.length;
                task.progress = (100 * task.current_size) / task.total_size;
                if (onProgress) {
                    onProgress({
                        loaded: task.current_size,
                        total: task.total_size,
                    });
                }
            }
            if (resp.getChunk?.isLast) {
                // console.log("isLast chunk received, breaking at iteraation", i + 1);
                break;
            }
        }
        // console.log("all chunk promises resolved, total data size:", data.length);

        //send one dummy getchunk to fix that isend issue
        //TODO:Remove this dummy req later
        // const resp = await new Promise<response.Response>((resolve, reject) => {
        //     const handler = (raw: Uint8Array) => {
        //         try {
        //             const decoded = response.Response.decode(raw);
        //             resolve(decoded);
        //         } catch (err) {
        //             reject(err);
        //         }
        //     };
        //     if (this.pendingRequests.has(dummy_req_id)) {
        //         console.log("Duplicate pending request id found for dummy req, generating new one");
        //         throw new Error(`Duplicate pending request id: ${dummy_req_id}`);
        //     }
        //     this.pendingRequests.set(dummy_req_id, handler);

        //     // timeout safety
        //     setTimeout(() => {
        //         if (this.pendingRequests.has(dummy_req_id)) {
        //             this.pendingRequests.delete(dummy_req_id);
        //             reject(new Error("Response timeout for dummy_req_id: " + dummy_req_id));
        //         }
        //     }, 500000);
        // });
        // console.log("dummy_getchunk_req resp");

        this.releaseConnection(conn);
        return new Uint8Array(data);
    }


    sendRawRequest(conn: PooledConnection, req: request.Request): string {
        const key = generateRequestId().toString();
        req.id = Long.fromString(key);
        const encoded = Request.encode(req).finish();
        conn.pendingTasks += 1;
        this.pendingRequests.set(key, (response) => {
            conn.pendingTasks -= 1;
        });
        conn.send(encoded);
        return key;
    }
    sendRawRequestWithoutIdInsertion(conn: PooledConnection, req: request.Request): string {
        const key = Long.fromValue(req.id ?? 0).toString();
        const encoded = Request.encode(req).finish();
        conn.pendingTasks += 1;
        conn.send(encoded);
        return key;
    }
    sendRawRequestWithoutCallBackInsertion(conn: PooledConnection, req: request.Request): string {
        const key = generateRequestId().toString();
        req.id = Long.fromString(key);
        const encoded = Request.encode(req).finish();
        conn.pendingTasks += 1;
        // this.pendingRequests.set(key, (response) => {
        //     conn.pendingTasks -= 1;
        // });
        conn.send(encoded);
        return key;
    }


    async putChunk(chunk_uuid: Uint8Array, versionUuid: Uint8Array, data: Uint8Array, offset: number, task: Task, onProgress: any) {
        if (this.isCancelled(task.getId())) {
            throw new Error("Task cancelled");
        }
        const fileHex = uint8ToHex(versionUuid);
        const conn = await this.getConnectionForPutChunk(fileHex);

        const totalSize = data.length;
        const blockSize = BLOCK_SIZE;

        const expectedIds: string[] = [];
        const blockSizes: number[] = [];

        try {
            // 1. Send START
            const startReq = request.Request.create({
                id: null,
                putChunk: request.PutChunk.create({
                    versionUuid,
                    uuid: chunk_uuid,
                    fileChunkOffset: offset,
                    flag: request.ChunkFlags.START,
                }),
            });
            expectedIds.push(this.sendRawRequest(conn, startReq));
            blockSizes.push(0);

            // 2. Send DATA blocks
            for (let blk_offset = 0; blk_offset < totalSize; blk_offset += blockSize) {
                const end = Math.min(blk_offset + blockSize, totalSize);
                const blockData = data.subarray(blk_offset, end);
                const dataReq = request.Request.create({
                    id: null,
                    putChunk: request.PutChunk.create({
                        versionUuid,
                        uuid: chunk_uuid,
                        fileChunkOffset: offset,
                        data: blockData,
                        flag: request.ChunkFlags.NONE,
                    }),
                });
                expectedIds.push(this.sendRawRequest(conn, dataReq));
                blockSizes.push(blockData.length);
            }

            // 3. Send STOP
            const stopReq = request.Request.create({
                id: null,
                putChunk: request.PutChunk.create({
                    versionUuid,
                    uuid: chunk_uuid,
                    fileChunkOffset: offset,
                    flag: request.ChunkFlags.STOP,
                }),
            });
            expectedIds.push(this.sendRawRequest(conn, stopReq));
            blockSizes.push(0);

            // 4. Wait for responses in order (TCP guarantees in-order on same connection)
            for (const id of expectedIds) {
                const resp = await new Promise<response.Response>((resolve, reject) => {
                    const handler = (raw: Uint8Array) => {
                        try {
                            const decoded = response.Response.decode(raw);
                            resolve(decoded);
                        } catch (err) {
                            reject(err);
                        }
                    };
                    this.pendingRequests.set(id, handler);

                    // timeout safety
                    setTimeout(() => {
                        if (this.pendingRequests.has(id)) {
                            this.pendingRequests.delete(id);
                            reject(new Error("Response timeout for id: " + id));
                        }
                    }, 500000);
                });

                if (resp.putChunk?.status !== 0) {
                    throw new Error(`Chunk failed with status ${resp.putChunk?.status}`);
                }
                task.current_size += blockSizes[expectedIds.indexOf(id)];
                task.progress = (100 * task.current_size) / task.total_size;
                if (onProgress) {
                    onProgress({
                        loaded: task.current_size,
                        total: task.total_size,
                    });
                }
            }
        } finally {
            this.releasePutChunkStream(conn, fileHex);
        }
    }


    /**
     * Send blocks from multiple chunks on the SAME connection.
     * All blocks are sent back-to-back keeping the connection saturated,
     * then all responses are awaited sequentially.
     */
    async putChunks(chunk_uuids: Uint8Array[], versionUuid: Uint8Array, datas: Uint8Array[], offsets: number[], task: Task, onProgress: any) {
        if (this.isCancelled(task.getId())) {
            throw new Error("Task cancelled");
        }
        const fileHex = uint8ToHex(versionUuid);
        // Get ONE connection for all chunks (same file, keeps it busy)
        const conn = await this.getConnectionForPutChunk(fileHex);

        const expectedIds: string[] = [];
        const blockSizes: number[] = [];

        try {
            // Send all blocks from all chunks back-to-back on the same connection
            for (let c = 0; c < datas.length; c++) {
                const data = datas[c];
                const offset = offsets[c];
                const chunk_uuid = chunk_uuids[c];
                const totalSize = data.length;
                const blockSize = BLOCK_SIZE;

                // START
                const startReq = request.Request.create({
                    id: null,
                    putChunk: request.PutChunk.create({
                        versionUuid,
                        uuid: chunk_uuid,
                        fileChunkOffset: offset,
                        flag: request.ChunkFlags.START,
                    }),
                });
                expectedIds.push(this.sendRawRequest(conn, startReq));
                blockSizes.push(0);

                // DATA blocks
                for (let blk_offset = 0; blk_offset < totalSize; blk_offset += blockSize) {
                    const end = Math.min(blk_offset + blockSize, totalSize);
                    const blockData = data.subarray(blk_offset, end);
                    const dataReq = request.Request.create({
                        id: null,
                        putChunk: request.PutChunk.create({
                            versionUuid,
                            uuid: chunk_uuid,
                            fileChunkOffset: offset,
                            data: blockData,
                            flag: request.ChunkFlags.NONE,
                        }),
                    });
                    expectedIds.push(this.sendRawRequest(conn, dataReq));
                    blockSizes.push(blockData.length);
                }

                // STOP
                const stopReq = request.Request.create({
                    id: null,
                    putChunk: request.PutChunk.create({
                        versionUuid,
                        uuid: chunk_uuid,
                        fileChunkOffset: offset,
                        flag: request.ChunkFlags.STOP,
                    }),
                });
                expectedIds.push(this.sendRawRequest(conn, stopReq));
                blockSizes.push(0);
            }

            // Wait for ALL responses sequentially
            for (const id of expectedIds) {
                const resp = await new Promise<response.Response>((resolve, reject) => {
                    const handler = (raw: Uint8Array) => {
                        try {
                            const decoded = response.Response.decode(raw);
                            resolve(decoded);
                        } catch (err) {
                            reject(err);
                        }
                    };
                    this.pendingRequests.set(id, handler);

                    setTimeout(() => {
                        if (this.pendingRequests.has(id)) {
                            this.pendingRequests.delete(id);
                            reject(new Error("Response timeout for id: " + id));
                        }
                    }, 500000);
                });

                if (resp.putChunk?.status !== 0) {
                    throw new Error(`Chunk failed with status ${resp.putChunk?.status}`);
                }
                task.current_size += blockSizes[expectedIds.indexOf(id)];
                task.progress = (100 * task.current_size) / task.total_size;
                if (onProgress) {
                    onProgress({
                        loaded: task.current_size,
                        total: task.total_size,
                    });
                }
            }
        } finally {
            this.releasePutChunkStream(conn, fileHex);
        }
    }

    async sendAndWait(req: request.Request): Promise<response.Response> {
        const conn = await this.getAvailableConnection();
        if (!conn) throw new Error("No available connection");
        const key = generateRequestId().toString();
        req.id = Long.fromString(key);
        const data = Request.encode(req).finish();
        conn.pendingTasks += 1;

        return new Promise<response.Response>((resolve, reject) => {
            let settled = false;
            const finalize = () => {
                if (settled) return;
                settled = true;
                this.unregisterPending(key);
                conn.pendingTasks -= 1;
            };

            const timeout = setTimeout(() => {
                if (settled) return;
                finalize();
                reject(new Error(`Request ${key} timed out after ${REQUEST_TIMEOUT_MS}ms`));
            }, REQUEST_TIMEOUT_MS);

            this.registerPending(
                key,
                conn.id,
                (raw) => {
                    clearTimeout(timeout);
                    finalize();
                    try {
                        resolve(Response.decode(raw));
                    } catch (err) {
                        reject(err as Error);
                    }
                },
                (err) => {
                    clearTimeout(timeout);
                    finalize();
                    reject(err);
                },
            );
            conn.send(data);
        });
    }

    /**
     * Fire all requests on a single exclusive connection without waiting between sends,
     * then collect all responses. This avoids the round-trip-per-request bottleneck.
     */
    async sendBatchAndWaitAll(requests: request.Request[]): Promise<response.Response[]> {
        const conn = await this.getAvailableConnection(true);
        if (!conn) throw new Error("No available connection");

        const promises: Promise<response.Response>[] = [];

        for (const req of requests) {
            const key = generateRequestId().toString();
            req.id = Long.fromString(key);
            const data = Request.encode(req).finish();
            conn.pendingTasks += 1;

            const promise = new Promise<response.Response>((resolve, reject) => {
                let settled = false;
                const finalize = () => {
                    if (settled) return;
                    settled = true;
                    this.unregisterPending(key);
                    conn.pendingTasks -= 1;
                };
                const timeout = setTimeout(() => {
                    if (settled) return;
                    finalize();
                    reject(new Error(`Batch request ${key} timed out`));
                }, REQUEST_TIMEOUT_MS);

                this.registerPending(
                    key,
                    conn.id,
                    (raw) => {
                        clearTimeout(timeout);
                        finalize();
                        try {
                            resolve(Response.decode(raw));
                        } catch (err) {
                            reject(err as Error);
                        }
                    },
                    (err) => {
                        clearTimeout(timeout);
                        finalize();
                        reject(err);
                    },
                );
            });

            conn.send(data);
            promises.push(promise);
        }

        try {
            return await Promise.all(promises);
        } finally {
            this.releaseConnection(conn);
        }
    }

    closeAll() {
        for (const conn of this.connections.values()) {
            conn.socket.close();
        }
        this.connections.clear();
        this.pendingRequests.clear();
    }
}

let globalPool: ConnectionPool;
export function setConnectionPool(pool: ConnectionPool) {
    globalPool = pool;
}
export async function getConnectionPool(): Promise<ConnectionPool> {

    while (initInProgress) {
        // Wait for the initialization to complete
        //sleep a bit
        await new Promise(resolve => setTimeout(resolve, 100));

    }


    if (!globalPool) {
        throw new Error("SDK not initialized. Call sdkInit() first.");
    }
    return globalPool;
}
export function getPin(): string {
    if (!globalPool) {
        throw new Error("SDK not initialized. Call sdkInit() first.");
    }
    return globalPool.getPin()
}
export function getToken(): Uint8Array {
    if (!globalPool) {
        throw new Error("SDK not initialized. Call sdkInit() first.");
    }
    return globalPool.getToken()
}
export function getEmail(): string {
    if (!globalPool) {
        throw new Error("SDK not initialized. Call sdkInit() first.");
    }
    return globalPool.getEmail()
}
